/**
 * Predicted — every match a prediction has been run for, across all leagues.
 *
 * No league filter and no top-ten limit, on purpose: a prediction was paid for, and this is the
 * screen that guarantees you can always find it.
 */
import { MatchCard } from '@/components/MatchCard';
import { Text } from '@/components/ui/text';
import { cn } from '@/lib/utils';
import { hasPrediction, kickoffMs } from '@/models';
import { Empty } from '@/screens/Upcoming';
import { useStore } from '@/store';
import { useLayout } from '@/useLayout';
import * as React from 'react';
import { ScrollView, View } from 'react-native';

export function PredictionsScreen() {
  const { wide } = useLayout();
  const { matches, stakes, placements, setStake, predict, place } = useStore();
  const [open, setOpen] = React.useState<string | null>(null);

  const shown = React.useMemo(
    () => matches.filter(hasPrediction).sort((a, b) => kickoffMs(a) - kickoffMs(b)),
    [matches],
  );

  const queued = matches.filter((m) => m.jobStatus === 'queued' || m.jobStatus === 'running').length;

  return (
    <ScrollView contentContainerClassName="gap-3 pb-6">
      <Text variant="small" className="text-muted-foreground">
        {shown.length} predicted
        {queued ? ` · ${queued} waiting on the gateway` : ''}
      </Text>

      {shown.length === 0 ? (
        <Empty
          title="No predictions yet"
          body="Run one from Upcoming. It queues for the AI gateway and lands here when the PC executor picks it up."
        />
      ) : (
        <View className={cn('gap-3', wide && 'flex-row flex-wrap')}>
          {shown.map((m) => (
            <View key={m.eventId} className={cn(wide && 'min-w-[380px] flex-1 basis-[380px]')}>
              <MatchCard
                match={m}
                dense={!wide}
                expanded={open === m.eventId}
                onToggleExpand={() => setOpen(open === m.eventId ? null : m.eventId)}
                stakes={Object.fromEntries(
                  Object.entries(stakes)
                    .filter(([k]) => k.startsWith(`${m.eventId}:`))
                    .map(([k, v]) => [k.split(':')[1], v]),
                )}
                placement={Object.fromEntries(
                  Object.entries(placements)
                    .filter(([k]) => k.startsWith(`${m.eventId}:`))
                    .map(([k, v]) => [k.split(':')[1], v]),
                )}
                onStake={(key, v) => setStake(m.eventId, key, v)}
                onPredict={() => void predict(m.eventId)}
                onPlace={(key) => void place(m.eventId, key)}
              />
            </View>
          ))}
        </View>
      )}
    </ScrollView>
  );
}
